<script setup lang="ts">
//#region imports
import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { addYears, endOfDay, format, isAfter, startOfDay } from 'date-fns';
import hash from 'object-hash';
import { useApi } from '@/api';
import { useActivitiesCommon } from '@/views/CRM/Activities/composables/useActivitiesCommon';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useLodash } from '@/setup/lodash';
import { useSegmentRules } from '@/views/CRM/composables/useSegmentRules';
import { useFilters } from '@/setup/filters';
import { useError } from '@/composables/useError';
import { useTypedStore } from '@/store';
import ActivitySegment from '@/views/CRM/Activities/components/ActivitySegment.vue';
import CrmErrorPanel from '@/views/CRM/components/CrmErrorPanel.vue';
import ActivityCheckControl from '@/views/CRM/Activities/components/ActivityCheckControl.vue';
import ActivityMetrics from '@/views/CRM/Activities/components/ActivityMetrics.vue';
import ActivityActions from '@/views/CRM/Activities/components/ActivityActions.vue';
import { useActivityActionsPolicy } from '@/views/CRM/Activities/composables/activityActionsPolicy';
import Comments from '@/components/Comments.vue';
import type { PostCrmActivitiesByIdCloneResponse, PutCommentsByIdData } from '@/types/openapi-ts';
import type { CrmActivityStatus } from '@/types/status';
import type {
  CrmActivity,
  CrmActivityDraft,
  CrmActivityMetric,
  CrmSegmentListItem,
  CrmTemplate,
  EditableCrmActivity,
} from '@/types/crm';
//#endregion

//#region types
interface Props {
  currentBrand?: string;
}

interface Emits {
  'update:currentBrand': [value: string];
}

interface ActionItem {
  icon: string;
  type: string;
  filled: boolean;
  text: string;
  action: () => void;
  states?: string[];
  placements: string[];
  showCondition?: () => boolean;
  disabled?: () => boolean;
  key?: number;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  currentBrand: '',
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const route = useRoute();
const router = useRouter();
const popupManager = usePopupManager();
const _ = useLodash();
const { t } = useI18n();
const filters = useFilters();
const { clearRules, clearSegment } = useSegmentRules();
const {
  confirmIndex,
  detectCrmStatus,
  isCanBeArchived,
} = useActivitiesCommon();

const { getError } = useError({
  errors: computed(() => {
    return errors.value || [];
  }),
});
//#endregion

//#region state
const activitySegmentRef = ref<any>(null);
const activityActionsRef = ref<any>(null);

const isLoading = ref(false);
const errors = ref<any>({});

const state = ref('none');
const id = ref('');
const activityHash = ref('');
const oldActivityHash = ref('');
const controlGroupActive = ref(true);
const oldControlGroupActive = ref(true);
const reworkState = ref<any>({});
const activity = ref<CrmActivity>({} as CrmActivity);
const oldActivity = ref<CrmActivity>({} as CrmActivity);
const needComment = ref('');
const shadowedActionGroups = ref<any[]>([]);
const permissions = ref<any>({});

const segments = ref<CrmSegmentListItem[]>([]);
const templates = ref<CrmTemplate[]>([]);

const controlGroupPercent = ref(100);
const controlGroupPlayers = ref(0);
const metricValues = ref<CrmActivityMetric[]>([]);
const now = ref(new Date());
const activityRange = ref<any>(null);
const playersPollInterval = ref<ReturnType<typeof setInterval> | null>(null);
//#endregion

//#region computed
const isEditRouteParam = computed(() => route.params.isEdit === 'true');

const calcActivityHashDebouced = computed(() => _.debounce(() => calcActivityHash(activity.value, 'activityHash'), 200));

const isSuperUser = computed(() => store.getters['auth/adminAcl'].is_superuser);

const isCreate = computed(() => !activity.value.id);
const isRunning = computed(() => activity.value.launch_status === 'running');
const canEdit = computed(() => isSuperUser.value || permissions.value.can_edit);
const canReview = computed(() => isSuperUser.value || permissions.value.can_review);
const canRun = computed(() => isSuperUser.value || permissions.value.can_run);
const isCanArchiveActivity = (payload: any) => isCanBeArchived(payload);
const status = computed(() => (isCreate.value ? 'draft' : detectCrmStatus(activity.value)));
const activityName = computed(() => (isCreate.value ? t('crm.activities.info.new_activity') : oldActivity.value.name));

const activityParams = computed(() => (activityRange.value ? activityRange.value : null));

const isFormValid = computed(() => {
  if (state.value === 'pause-edit') {
    return activityHash.value !== oldActivityHash.value
      || controlGroupActive.value !== oldControlGroupActive.value;
  }

  return true;
});

const isApproved = computed(() => {
  let isActionGroupsApproved = true;

  (reworkState.value.action_groups || []).forEach((actionGroup: any) => {
    if (actionGroup.is_control && !controlGroupActive.value) {
      return;
    }

    if (!actionGroup.is_approved) {
      isActionGroupsApproved = false;
    } else {
      (actionGroup.actions || []).forEach((action: any) => {
        if (!action.is_approved) {
          isActionGroupsApproved = false;
        }
      });
    }
  });

  return reworkState.value.is_approved_general
    && (reworkState.value.is_approved_segment || !permissions.value.can_view_segment)
    && reworkState.value.is_approved_actions
    && isActionGroupsApproved;
});

const isFullPauseEdit = computed(() =>
  state.value === 'pause-edit' && actionModes.value.isFullEditMode,
);

const actionsList = computed<ActionItem[]>(() => {
  const actions: ActionItem[] = [
    {
      icon: 'play',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.start_activity'),
      action: () => handleStart(),
      states: ['approved'],
      placements: ['header', 'footer'],
      showCondition: () => !isRunning.value && status.value === 'approved' && canRun.value,
    },
    {
      icon: 'play',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.resume_activity'),
      action: () => handleStart(),
      states: ['approved'],
      placements: ['header', 'footer'],
      showCondition: () => !isRunning.value && status.value === 'paused' && canRun.value,
    },
    {
      icon: 'pause',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.pause_activity'),
      action: () => handlePause(),
      states: ['approved'],
      placements: ['header', 'footer'],
      showCondition: () => isRunning.value && status.value !== 'completed' && canRun.value,
    },
    {
      icon: 'stop',
      type: 'negative',
      filled: true,
      text: t('crm.buttons.stop_activity'),
      action: () => handleComplete(),
      states: ['approved'],
      placements: ['header', 'footer'],
      showCondition: () => status.value !== 'completed' && status.value !== 'approved' && canRun.value,
    },
    {
      icon: 'check-double',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.save_and_send'),
      action: () => handleSaveAndSendToReview(),
      disabled: () => !isFormValid.value,
      states: ['rework-edit', 'edit', 'pause-edit'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'check',
      type: 'positive',
      filled: false,
      text: t('crm.buttons.save'),
      action: () => handleSave(),
      disabled: () => !isFormValid.value,
      states: ['edit', 'rework-edit'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'times',
      type: 'negative',
      filled: false,
      text: t('crm.buttons.cancel'),
      action: () => handleCancel(),
      states: ['edit', 'rework-edit', 'pause-edit'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'check',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.approve'),
      action: () => handleApprove(),
      disabled: () => !isApproved.value,
      showCondition: () => canReview.value,
      states: ['check'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'sync-alt',
      type: 'positive',
      filled: false,
      text: t('crm.buttons.for_rework'),
      action: () => handleSendForRework(),
      disabled: () => !!isApproved.value,
      showCondition: () => canReview.value,
      states: ['check'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'times',
      type: 'negative',
      filled: false,
      text: t('crm.buttons.decline'),
      action: () => handleDecline(),
      showCondition: () => canReview.value,
      states: ['check'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'check-double',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.send'),
      action: () => handleSendToReview(),
      showCondition: () => canEdit.value,
      disabled: () => !isFormValid.value,
      states: ['preview-edit'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'check-double',
      type: 'positive',
      filled: true,
      text: t('crm.buttons.send'),
      action: () => handleSendToReview(),
      showCondition: () => canEdit.value && activity.value.comment_needed === false,
      disabled: () => !isFormValid.value,
      states: ['rework'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'pen',
      type: 'positive',
      filled: false,
      text: t('crm.buttons.edit'),
      action: () => handleEdit(),
      placements: ['header', 'footer'],
      states: ['rework', 'approved'],
      showCondition: () => {
        if (state.value === 'approved') {
          return !isRunning.value && status.value === 'paused' && canEdit.value;
        }

        return canEdit.value;
      },
    },
    {
      icon: 'pen',
      type: 'positive',
      filled: false,
      text: t('crm.buttons.edit'),
      action: () => handleEdit(),
      showCondition: () => canEdit.value,
      states: ['preview-edit'],
      placements: ['header', 'footer'],
    },
    {
      icon: 'clone',
      type: 'positive',
      filled: false,
      text: t('crm.buttons.clone'),
      action: () => handleClone(),
      placements: ['header', 'footer'],
      showCondition: () => canEdit.value && activity.value.moderation_status === 'approved',
    },
    {
      icon: 'trash-alt',
      type: 'negative',
      filled: false,
      text: t('crm.buttons.archive'),
      action: () => handleArchive(),
      disabled: () => !isCanArchiveActivity(activity.value),
      states: ['approved', 'preview', 'preview-edit'],
      placements: ['header', 'footer'],
      showCondition: () => canEdit.value && isCanArchiveActivity(activity.value)
        && ((state.value === 'preview' && status.value === 'declined') || state.value !== 'preview'),
    },
  ];

  return actions.map((item, index) => ({ ...item, key: index }));
});

const actionsFiltered = computed(() => actionsList.value
  .filter((item) => !item.showCondition || item.showCondition())
  .filter((item) => {
    if (!item.states) return true;
    return item.states.includes(state.value);
  }));
const actionsHeader = computed(() => actionsFiltered.value.filter((item) => item.placements.includes('header')));
const actionsFooter = computed(() => actionsFiltered.value.filter((item) => item.placements.includes('footer')));

const backPage = computed(() => {
  if (route.query.from === 'running') {
    return {
      title: t('crm.activities.title_running'),
      link: '/crm/running',
    };
  }

  if (route.query.from === 'quality_assurance') {
    return {
      title: t('crm.activities.title_quality_assurance'),
      link: '/crm/quality-assurance/activities',
    };
  }

  return {
    title: t('crm.activities.title'),
    link: '/crm/activities/list',
  };
});

const breadcrumbs = computed(() => {
  return [
    backPage.value,
    {
      title: activityName.value,
    },
  ];
});

const periodError = computed(() => getError('activity_period_start') || getError('activity_period_end'));
const nameError = computed(() => getError('name'));
const periodExpiredError = computed(() => {
  const hasError = state.value === 'check' && isAfter(now.value, activity.value.activity_period_end);
  return hasError && t('crm.activities.errors.activity_expired_error');
});
const generalErrors = computed(() => [periodExpiredError.value].filter((item) => !!item));

const period = computed(() => {
  const { activity_period_start, activity_period_end } = activity.value;
  const valueFrom = filters.formatDate3(activity_period_start);
  const valueTo = filters.formatDate3(activity_period_end);

  return `${valueFrom} - ${valueTo}`;
});
const { modes: actionModes } = useActivityActionsPolicy({
  activity,
  state,
  controlGroupActive,
  canReview,
});

const editableActivity = computed(() => activity.value as unknown as EditableCrmActivity);
//#endregion

//#region methods
function getSiteId() {
  return activity.value.site_id || props.currentBrand;
}

function getTemplates() {
  return api
    .getEmailTemplates({
      site_id: getSiteId(),
    })
    .then((response: any) => {
      templates.value = _.cloneDeep(response.data.payload);
    });
}

function getSegments() {
  return api
    .getSegments({
      site_id: getSiteId(),
      // schema: 'short_list',
      moderation_status: 'approved',
    })
    .then((response: any) => {
      segments.value = _.cloneDeep(response.data.payload);
    });
}

async function initComponent() {
  id.value = route.params.id as string;
  isLoading.value = true;

  if (id.value) {
    await getActivity();

    if (activity.value.site_id !== props.currentBrand) {
      emit('update:currentBrand', activity.value.site_id);
    }
  } else {
    activity.value = getDefaultActivity();
    saveComponentState();
    state.value = 'edit';
  }

  await Promise.all([getSegments(), getTemplates()]);

  if (isEditRouteParam.value) {
    state.value = 'edit';
  }

  await nextTick(() => {
    const segmentStats = activitySegmentRef.value?.getDefaultSegmentStats();
    if (!segmentStats) return;

    segmentStats.players_count = activity.value.players_count;

    activitySegmentRef.value?.setSegmentId(activity.value.crm_segment_id, false, segmentStats);
    if (segments.value.length === 0 && activity.value.crm_segment_id) {
      const currentSegment = {
        id: activity.value.crm_segment_id,
        is_global: activity.value.crm_segment_is_global,
        name: activity.value.crm_segment_name,
        players_count: activity.value.crm_segment_players_count,
        type: activity.value.crm_segment_type,
        filters: activity.value.filters,
        moderation_status: 'approved',
      } as unknown as CrmSegmentListItem;
      segments.value.push(currentSegment);
    }
  });

  isLoading.value = false;
}

const getDefaultPeriod = () => ({
  from: startOfDay(new Date()),
  to: addYears(endOfDay(new Date()), 1),
});

const getDefaultActivity = (): CrmActivity => {
  const { from, to } = getDefaultPeriod();

  const draft: CrmActivityDraft = {
    name: '',
    site_id: props.currentBrand,
    activity_period_start: format(from, 'yyyy-MM-dd 00:00:00'),
    activity_period_end: format(to, 'yyyy-MM-dd 23:59:59'),
    crm_segment_id: '',
    crm_segment: {},
    send_actions_repeat_interval: 1,
    send_actions_count: 1,
    send_actions_period: 'day',
    send_actions_repeat: false,
    action_groups: [
      {
        index: 0,
        is_control: true,
        percent: 100,
      },
    ],
    metrics: [],
    metrics_has_cooldown: false,
    metrics_cooldown: 7,
    metrics_updated_at: format(from, 'HH:mm dd.MM.yyyy'),
    created_at: format(from, 'yyyy-MM-dd 00:00:00'),
    filters: [],
  };

  return draft as unknown as CrmActivity;
};

function getActivity() {
  return Promise.all([
    () => api
      .getActivity(id.value, activityParams.value)
      .then((response: any) => {
        activity.value = _.cloneDeep(response.data.payload);

        if (!activity.value.is_players_attached) {
          getPlayers();
        }

        permissions.value = _.cloneDeep(response.data.misc.permissions);
        const defaultActivity = getDefaultActivity();

        activity.value.metrics_has_cooldown = activity.value.metrics_cooldown !== null;

        if (activity.value.metrics_has_cooldown) {
          activity.value.metrics_cooldown = secondsToDays(activity.value.metrics_cooldown || 0);
        } else {
          activity.value.metrics_cooldown = defaultActivity.metrics_cooldown;
        }

        const sendActionsRepeatInterval = activity.value.send_actions_repeat_interval || 0;
        activity.value.send_actions_repeat_interval = secondsToDays(sendActionsRepeatInterval);

        const controlGroup = activity.value.action_groups.find((item: any) => item.is_control);
        controlGroupActive.value = !!controlGroup;
        controlGroupPlayers.value = controlGroup?.players_count || 0;
        controlGroupPercent.value = controlGroup ? controlGroup.percent : 0;

        if (!controlGroupActive.value) {
          activity.value.action_groups.unshift({
            index: 0,
            is_control: true,
            percent: 0,
            created_at: null,
            updated_at: null,
            id: '',
            incrementing_id: 0,
            players_count: 0,
            crm_activity_id: '',
            is_approved: false,
            players_version: null,
            actions: [],
          });
        }

        if (activity.value.metrics.length > 0) {
          const hasMetricsValues = typeof activity.value.metrics[0] === 'object';
          const metrics = hasMetricsValues
            ? activity.value.metrics.map((metric: any) => metric.name)
            : _.cloneDeep(activity.value.metrics);

          if (hasMetricsValues) {
            metricValues.value = _.cloneDeep(activity.value.metrics);
          }

          activity.value.metrics = metrics;
        }

        if (activity.value.metrics_updated_at) {
          activity.value.metrics_updated_at = format(activity.value.metrics_updated_at, 'HH:mm dd.MMM.yyyy');
        }

        now.value = new Date();
        saveComponentState();

        switch (status.value) {
          case 'draft':
            state.value = 'preview-edit';
            break;
          case 'on_review':
            state.value = 'check';
            reworkState.value = getReworkState();
            break;
          case 'approved':
          case 'running':
          case 'paused':
          case 'completed':
            state.value = 'approved';
            break;
          case 'for_rework':
            state.value = 'rework';
            reworkState.value = getReworkState();
            break;
          case 'archived':
          case 'declined':
          default:
            state.value = 'preview';
        }
      })
      .catch((err: any) => {
        if (err.status === 404) {
          router.push('/crm/activities/list');
        }
      }),
  ].map((defered) => defered()));
}

function openConfirmPopup(confirmPayload: any) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: confirmPayload.title,
      message: confirmPayload.msg,
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: confirmPayload.type === 'warning' ? 'var(--text-negative)' : 'var(--main-color)',
      confirmTitle: confirmPayload.actionName,
      confirmType: confirmPayload.type === 'warning' ? 'negative' : 'positive',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await confirmPayload.confirmAction();
        } finally {
          context.handleClose();
        }
      },
    },
  });
}

const startPlayersPolling = () => {
  if (activity.value.is_players_attached) {
    activity.value.is_players_attached = false;
  }
  getPlayers();
};

async function changeStatus(newStatus: CrmActivityStatus, payload?: any) {
  isLoading.value = true;

  try {
    const { data } = await api.postActivityStatus(activity.value.id, newStatus, payload);

    switch (newStatus) {
      case 'clone':
        return router.push({
          name: 'crm.activity',
          params: {
            id: (data as unknown as PostCrmActivitiesByIdCloneResponse).payload.id,
            isEdit: 'true',
          },
        });
      default:
        await getActivity();

        if (newStatus === 'approve') {
          startPlayersPolling();
        }
    }
  } finally {
    isLoading.value = false;
  }
}

function openChangeStatusConfirm(newStatus: CrmActivityStatus, payload?: any) {
  const indexMap = confirmIndex.value as Record<string, (arg1: any, arg2: () => Promise<any>) => any>;
  const handler = indexMap[newStatus];
  if (!handler) return;

  const confirmPayload = handler(activity.value, () => changeStatus(newStatus, payload));
  openConfirmPopup(confirmPayload);
}

function handleStart() {
  openChangeStatusConfirm('run');
}

function handlePause() {
  openChangeStatusConfirm('pause');
}

function handleComplete() {
  openChangeStatusConfirm('complete');
}

function handleDecline() {
  openChangeStatusConfirm('decline');
}

function handleClone() {
  openChangeStatusConfirm('clone');
}

function handleArchive() {
  openChangeStatusConfirm('archive');
}

async function handleSave() {
  await save();
}

function getReworkStatePayload() {
  return {
    ...reworkState.value,
    action_groups: reworkState.value.action_groups
      .filter((actionGroup: any) => controlGroupActive.value || !actionGroup.is_control),
  };
}

async function handleSaveAndSendToReview() {
  const indexMap = confirmIndex.value;
  const confirmPayload = indexMap['save-and-send-to-review'](activity.value, async () => {
    await save(state.value !== 'pause-edit' || status.value === 'for_rework');
  });
  openConfirmPopup(confirmPayload);
}

async function handleSendToReview() {
  const indexMap = confirmIndex.value;
  const confirmPayload = indexMap['send-to-review'](activity.value, async () => {
    await sendToReview();
  });
  openConfirmPopup(confirmPayload);
}

function handleSendForRework() {
  openChangeStatusConfirm('for-rework', getReworkStatePayload());
}

function onChangeActivityRange(date: any) {
  activityRange.value = date
    ? {
      stats_period_start: format(date[0], 'yyyy-MMM-dd HH:mm:ss'),
      stats_period_end: format(endOfDay(date[1]), 'yyyy-MMM-dd HH:mm:ss'),
    }
    : null;

  getActivity();
}


function handleApprove() {
  openChangeStatusConfirm('approve', getReworkStatePayload());
}

function handleEdit() {
  if (state.value === 'rework') {
    state.value = 'rework-edit';
  } else if (status.value === 'paused' || activity.value.launch_status === 'paused') {
    state.value = 'pause-edit';
  } else if (state.value === 'preview-edit') {
    state.value = 'edit';
  }
}

function handleCancel() {
  if (isCreate.value) {
    router.push(backPage.value.link);
  } else {
    activity.value = _.cloneDeep(oldActivity.value);
    controlGroupActive.value = oldControlGroupActive.value;

    if (state.value === 'rework-edit' || (status.value === 'for_rework' && activity.value.launch_status === 'paused')) {
      state.value = 'rework';
    } else if (state.value === 'edit') {
      state.value = 'preview-edit';
    } else if (state.value === 'pause-edit') {
      state.value = 'approved';
    }

    errors.value = {};

    nextTick(() => {
      activityActionsRef.value?.restoreState();

      const segmentStats = activitySegmentRef.value?.getDefaultSegmentStats();
      segmentStats.players_count = activity.value.players_count;

      activitySegmentRef.value?.setSegmentId(activity.value.crm_segment_id, true, segmentStats);
    });

    needComment.value = '';
  }
}

async function sendToReview() {
  isLoading.value = true;
  needComment.value = '';
  try {
    await api.postActivityStatus(id.value, 'to-review');
    await getActivity();
  } catch (error: any) {
    if (error.data && error.data.code === 'CRM_ACTIVITY_COMMENT_NEEDED_ERROR') {
      needComment.value = error.data.message;
    }
  } finally {
    isLoading.value = false;
  }
}

function prepareActivity(sourceActivity: any) {
  const _activity = _.cloneDeep(sourceActivity);
  const isGlobal = activitySegmentRef.value?.segmentType === 'global';

  if (isGlobal) {
    delete _activity.crm_segment;
  } else {
    const { segment } = activitySegmentRef.value;
    _activity.crm_segment = segment ? clearSegment(segment) : segment;

    delete _activity.crm_segment_id;
    delete _activity.filters;
  }

  if (_activity.filters && _activity.filters.length) {
    _activity.filters = clearRules(_activity.filters);
  }

  _activity.action_groups = shadowedActionGroups.value
    .map((actionGroup: any) => ({
      ...actionGroup,
      percent: +actionGroup.percent,
      actions: (actionGroup.actions || []).map((action: any) => ({
        ...action,
        is_default: (actionGroup.actions || []).length === 1 || action.is_default,
      })),
    }));

  if (!controlGroupActive.value) {
    _activity.action_groups = _activity.action_groups.filter((item: any) => !item.is_control);
  }

  const sendActionsRepeatInterval = _activity.send_actions_repeat_interval || 0;
  _activity.send_actions_repeat_interval = daysToSeconds(sendActionsRepeatInterval);

  if (!_activity.metrics_has_cooldown) {
    _activity.metrics_cooldown = null;
  } else {
    _activity.metrics_cooldown = daysToSeconds(_activity.metrics_cooldown);
  }

  delete _activity.metrics_has_cooldown;

  return _activity;
}

async function save(toReview = false) {
  errors.value = {};
  isLoading.value = true;

  const preparedActivity = prepareActivity(activity.value);
  needComment.value = '';

  try {
    const promise = id.value
      ? api.putActivity(id.value, preparedActivity)
      : api.postActivity(preparedActivity);

    const response = await promise.catch((localErrors: any) => {
      if (localErrors.data.code === 'WRONG_FILE_FORMAT_ERROR') {
        errors.value.crm_segment = {
          players_file_id: localErrors.data,
        };
      }

      if (localErrors.data.code === 'VALIDATION_ERROR') {
        errors.value = localErrors.data.errors;
        if (errors.value.action_groups && !controlGroupActive.value) {
          errors.value.action_groups.unshift(null);
        }
      }

      throw localErrors;
    });

    const createdId = _.get(response, 'data.payload.id', null);
    const cb = async () => {
      if (toReview) {
        const { status, moderation_status } = response.data.payload || {};
        const isAlreadyOnReview = status === 'active' && moderation_status === 'on_review';

        if (!isAlreadyOnReview) {
          await api.postActivityStatus(id.value, 'to-review');
        }
      }

      await getActivity();

      startPlayersPolling();
      activitySegmentRef.value?.getLocalSegment();
    };

    if (isCreate.value && createdId) {
      id.value = createdId;
      router.push(`/crm/activity/${createdId}`)
        .then(cb)
        .catch(cb);
    } else {
      await cb();
    }
  } catch (error: any) {
    if (error.data && error.data.code === 'CRM_ACTIVITY_COMMENT_NEEDED_ERROR') {
      needComment.value = error.data.message;
    }
  } finally {
    isLoading.value = false;
  }
}

function secondsToDays(seconds: number) {
  return seconds / (24 * 60 * 60);
}

function daysToSeconds(days: number) {
  return days * (24 * 60 * 60);
}

function getReworkState() {
  const actionGroups = activity.value.action_groups.map((actionGroup: any) => {
    const result: any = {
      id: actionGroup.id,
      is_control: actionGroup.is_control,
      is_approved: !!actionGroup.is_approved,
    };

    if (actionGroup.actions) {
      result.actions = actionGroup.actions.map((action: any) => ({
        id: action.id,
        is_approved: !!action.is_approved,
      }));
    }

    return result;
  });

  return {
    is_approved_general: !!activity.value.is_approved_general,
    is_approved_segment: !!activity.value.is_approved_segment,
    is_approved_actions: !!activity.value.is_approved_actions,
    action_groups: actionGroups,
  };
}

const stopPlayersPoll = () => {
  if (playersPollInterval.value) {
    clearInterval(playersPollInterval.value);
    playersPollInterval.value = null;
  }
};

function getPlayers() {
  stopPlayersPoll();

  playersPollInterval.value = setInterval(async () => {
    const { data: { payload } } = await api.getActivity(id.value, {});

    if (payload.is_players_attached || !route.params.id) {
      if (route.params.id) {
        activity.value.is_players_attached = payload.is_players_attached;
        activity.value.players_count = payload.players_count;

        activity.value.action_groups.forEach((action: any) => {
          action.players_count = payload.action_groups
            .find(({ id: actionId }: any) => actionId === action.id)?.players_count || action.players_count;
        });

        const controlGroup = activity.value.action_groups.find((item: any) => item.is_control);
        controlGroupPlayers.value = controlGroup?.players_count || 0;
      }

      stopPlayersPoll();
    }
  }, 15000);
}

function saveComponentState() {
  nextTick(() => {
    oldActivity.value = _.cloneDeep(activity.value);
    activityActionsRef.value?.saveState();
    oldControlGroupActive.value = controlGroupActive.value;

    const segmentStats = activitySegmentRef.value?.getDefaultSegmentStats();
    activitySegmentRef.value?.setSegmentId(activity.value.crm_segment_id, false, segmentStats);
  });
}

function calcActivityHash(targetActivity: any, hashName: 'activityHash' | 'oldActivityHash') {
  const result = hash(targetActivity, { algorithm: 'md5' });
  if (hashName === 'activityHash') {
    activityHash.value = result;
  } else {
    oldActivityHash.value = result;
  }
}

function getComments() {
  const params = {
    sort_column: 'created_at',
    sort_dir: 'asc',
  } as const;

  return  api.getActivityComments(id.value, params);
}

function postComment(comment: PutCommentsByIdData['body']) {
  return api.postActivityComment(id.value, comment);
}
//#endregion

//#region watch
watch(activity, () => {
  calcActivityHashDebouced.value();
}, { deep: true, immediate: true });

watch(oldActivity, () => {
  calcActivityHash(oldActivity.value, 'oldActivityHash');
}, { deep: true, immediate: true });

watch(() => props.currentBrand, async (newBrand) => {
  if (activity.value.site_id === newBrand) return;

  await router.push('/crm/activities/list');
});

watch(() => route.params.isEdit, (value) => {
  if (value === 'true') {
    initComponent();
  }
});
//#endregion

onBeforeMount(() => {
  initComponent();
});

onBeforeUnmount(() => {
  stopPlayersPoll();
});
</script>

<template>
  <div
    v-loading="isLoading"
    class="crm-activity"
  >
    <div class="t:space-y-16 t:mb-24">
      <Breadcrumbs :items="breadcrumbs" />
      <div class="t:flex t:items-end t:justify-between t:mb-12">
        <div class="t:space-y-8">
          <div class="crm-activity__title">
            {{ activityName }}
          </div>
          <div class="crm-activity__info">
            <div class="crm-activity__info-item">
              <div class="crm-activity__info-title">
                {{ $t('crm.activities.info.status') }}:
              </div>
              <UiStatusText
                :type="status"
                :text="$t(`crm.fields.computed_status.values.${status}`)"
              />
            </div>
            <div class="crm-activity__info-item">
              <div class="crm-activity__info-title">
                {{ $t('crm.activities.info.created_at') }}:
              </div>
              {{ $filters.formatDate3(activity.created_at || '') }}
            </div>
            <div
              v-if="activity.moderation_status === 'approved' || activity.status === 'archived'"
              class="crm-activity__info-item"
            >
              <div class="crm-activity__info-title">
                {{ $t('crm.activities.info.period') }}:
              </div>
              {{ period }}
            </div>
          </div>
        </div>
        <div class="t:flex t:gap-8">
          <UiButton
            v-for="action in actionsHeader"
            :key="action.key"
            :icon="action.icon"
            :type="action.type"
            :filled="action.filled"
            :disabled="action.disabled && action.disabled()"
            @click="action.action()"
          >
            {{ action.text }}
          </UiButton>
        </div>
      </div>
      <UiSeparator />
    </div>
    <div class="crm-activity__content t:space-y-24">
      <ActivityMetrics
        v-if="state === 'approved' || state === 'preview'"
        :can-edit="canEdit && !['archived', 'completed', 'declined'].includes(status)"
        :activity="editableActivity"
        :control-group-active="controlGroupActive"
        preview-mode
        :metric-values="metricValues"
        :errors="errors"
        @change-activity-range="onChangeActivityRange"
      />
      <FormRow
        v-if="(activity.moderation_status !== 'approved' && activity.status !== 'archived') || state === 'pause-edit'"
        class="crm-activity__row"
        :error="!!(nameError || periodError || generalErrors.length)"
      >
        <div class="t:space-y-16">
          <template
            v-if="state === 'edit' || state === 'rework-edit' || state === 'pause-edit'"
          >
            <UiLabel
              :label="$t('crm.activities.info.general')"
              :font-size="20"
              bold
            />
            <div class="t:flex t:gap-12">
              <UiInput
                v-model="activity.name"
                :label="$t('crm.activities.info.name')"
                :placeholder="$t('crm.activities.info.name_placeholder')"
                class="t:flex-1"
                size="big"
                type="text"
                :error="nameError"
              />
              <div class="crm-activity__date-block">
                <UiDatePicker
                  v-model="activity.activity_period_start"
                  :label="$t('crm.activities.info.start_date')"
                  :placeholder="$t(`team_popup.see_stats_placeholder`)"
                  :multi-calendars="false"
                  :range="false"
                  external-format="dd MMM yyyy HH:mm"
                  model-type="yyyy-MM-dd HH:mm:ss"
                  enable-time-picker
                  custom-input
                  size="big"
                />
                <UiDatePicker
                  v-model="activity.activity_period_end"
                  :label="$t('crm.activities.info.end_date')"
                  :placeholder="$t(`team_popup.see_stats_placeholder`)"
                  :multi-calendars="false"
                  :range="false"
                  external-format="dd MMM yyyy HH:mm"
                  model-type="yyyy-MM-dd HH:mm:ss"
                  enable-time-picker
                  custom-input
                  size="big"
                />
              </div>
            </div>
          </template>
          <template
            v-else
          >
            <UiLabel
              :label="$t('crm.activities.info.period')"
              :font-size="20"
              bold
            />
            <Panel>
              <template v-slot:prepend>
                <ActivityCheckControl
                  v-if="state === 'rework' || (state === 'check' && canReview)"
                  v-model="reworkState.is_approved_general"
                  :can-edit="state === 'check'"
                />
              </template>
              <div class="t:flex t:items-center t:space-x-8">
                <UiIcon
                  :size="16"
                  substyle="far"
                  name="calendar-alt"
                />
                <div>
                  {{ period }}
                </div>
              </div>
            </Panel>
          </template>


          <div
            v-if="generalErrors.length > 0"
            class="t:flex t:flex-col t:items-start t:space-y-8"
          >
            <CrmErrorPanel
              v-for="(error, index) in generalErrors"
              :key="index"
            >
              {{ error }}
            </CrmErrorPanel>
          </div>
        </div>
      </FormRow>
      <ActivitySegment
        ref="activitySegmentRef"
        :state="state"
        :activity="activity"
        :old-activity="oldActivity"
        :loading="isLoading"
        :segments="segments"
        :view-segment="isSuperUser || permissions.can_view_segment"
        :can-review="canReview"
        :is-create="isCreate"
        :current-brand="currentBrand"
        :rework-state="reworkState"
        :errors="errors"
      />
      <ActivityActions
        ref="activityActionsRef"
        v-model:control-group-active="controlGroupActive"
        v-model:control-group-percent="controlGroupPercent"
        v-model:control-group-players="controlGroupPlayers"
        class="t:mb-16"
        :state="state"
        :activity="activity"
        :templates="templates"
        :loading="isLoading"
        :rework-state="reworkState"
        :current-brand="currentBrand"
        :can-review="canReview"
        :errors="errors"
        :activity-range="activityRange"
        @update-shadowed-groups="shadowedActionGroups = $event"
      />
      <ActivityMetrics
        v-if="['edit', 'rework-edit', 'preview-edit', 'check', 'rework'].includes(state) || isFullPauseEdit"
        :activity="editableActivity"
        :control-group-active="controlGroupActive"
        :metric-values="metricValues"
        :can-edit="['edit', 'rework-edit'].includes(state) || isFullPauseEdit"
        :preview-mode="['preview-edit', 'check', 'rework'].includes(state)"
        :errors="errors"
      />
      <div class="t:space-y-4">
        <Comments
          v-if="!isCreate"
          :is-editable="canEdit"
          :get-method="getComments"
          :post-method="postComment"
        />
        <CrmErrorPanel
          v-if="needComment"
        >
          {{ needComment }}
        </CrmErrorPanel>
      </div>
      <FormRow
        v-if="(state === 'edit' || state === 'check' || state === 'rework-edit') && actionsFooter.length > 0"
        class="crm-activity__panel"
      >
        <div class="t:flex t:space-x-8">
          <div
            v-for="action in actionsFooter"
            :key="action.key"
          >
            <UiButton
              :icon="action.icon"
              :type="action.type"
              :filled="action.filled"
              size="big"
              :disabled="action.disabled && action.disabled()"
              @click="action.action"
            >
              {{ action.text }}
            </UiButton>
          </div>
        </div>
      </FormRow>
    </div>
  </div>
</template>

.<style lang="scss">
.crm-activity{
  &__title {
    color: var(--text-body);
    font-size: 24px;
    font-weight: 500;
    line-height: 120%;
  }
  &__info{
    display: flex;
    align-items: center;
  }
  &__content {
    min-width: 960px;
    max-width: 1440px;
    margin: 0 auto;
  }
  &__info-item{
    display: flex;
    align-items: center;
    color: var(--text-body);
    font-size: 12px;
    font-weight: 400;
    &:after {
      content: '';
      width: 4px;
      height: 4px;
      border-radius: 8px;
      background: var(--background-primary);
      margin: 0 8px;
    }
    &:last-child:after {
      display: none;
    }
  }
  &__info-title{
    color: var(--text-body-secondary);
    margin-right: 2px;
  }
  &__row.form-row {
    padding: 16px 24px;
  }
  &__date-block {
    display: flex;
    min-width: 340px;
    gap: 12px;
  }
}
</style>
